<template>
  <div class="param-value-link">
    <div class="tables-container">
      <div class="table-block">
        <CustomTable
          title="Основной параметр"
          :rowsData="visibleLeftRows"
          :columns="leftColumns"
          :loading="loadingParams"
          :show-search="true"
          :custom-body="true"
          :sticky-header="true"
        >
          <template #top-right>
            <q-checkbox
              v-model="leftFilterHasLinksOnly"
              size="sm"
              dense
              label="Только со связками"
            />
          </template>
          <template #customBody="props">
            <q-tr
              :class="[
                'clickable-row',
                { 'selected-row': selectedParam && selectedParam.param_id === props.row.param_id },
                { 'has-links-row': hasLinks(props.row) }
              ]"
              @click="onSelectParam(props.row)"
            >
              <custom-table-cell align="center">
                {{ props.row.param_id }}
              </custom-table-cell>
              <custom-table-cell align="left">
                {{ props.row.param_code }}
              </custom-table-cell>
              <custom-table-cell align="left">
                {{ props.row.param_name }}
              </custom-table-cell>
            </q-tr>
          </template>
        </CustomTable>
      </div>

      <div class="table-block">
        <CustomTable
          title="Связанные параметры"
          :rowsData="visibleRightRows"
          :columns="rightColumns"
          :loading="loadingLinks || bulkInProgress"
          :show-search="false"
          :custom-body="true"
          :custom-header="true"
          :sticky-header="true"
        >
          <template #top-right>
            <div class="right-filters">
              <q-input
                v-model="rightSearch"
                dense
                outlined
                placeholder="Поиск..."
                clearable
                class="right-filters__search"
              >
                <template #prepend>
                  <q-icon name="search" size="16px" />
                </template>
              </q-input>
              <q-checkbox
                v-model="rightFilterLinkedOnly"
                size="sm"
                dense
                label="Только связанные"
                :disable="!selectedParam"
              />
            </div>
          </template>
          <template #customHeader="props">
            <q-tr>
              <q-th auto-width>
                <q-checkbox
                  :model-value="isAllLinked"
                  @update:model-value="onToggleAll"
                  size="sm"
                  dense
                  :disable="!selectedParam || saving || bulkInProgress"
                />
              </q-th>
              <q-th
                v-for="col in rightColumns"
                :key="col.name"
                :props="props"
              >
                {{ col.label }}
              </q-th>
            </q-tr>
            <q-tr v-if="bulkInProgress">
              <q-th :colspan="rightColumns.length + 1">
                <div class="bulk-progress">
                  <q-linear-progress
                    :value="bulkProgress"
                    color="primary"
                    size="8px"
                    rounded
                    class="bulk-progress__bar"
                  />
                  <span class="bulk-progress__text">
                    {{ bulkDone }} / {{ bulkTotal }}
                  </span>
                </div>
              </q-th>
            </q-tr>
          </template>
          <template #customBody="props">
            <q-tr>
              <custom-table-cell align="center">
                <q-checkbox
                  :model-value="isLinked(props.row)"
                  @update:model-value="(val) => onToggleLink(props.row, val)"
                  @click.stop
                  size="sm"
                  :disable="!selectedParam || props.row.param_id === selectedParam?.param_id || saving || bulkInProgress"
                />
              </custom-table-cell>
              <custom-table-cell align="center">
                {{ props.row.param_id }}
              </custom-table-cell>
              <custom-table-cell align="left">
                {{ props.row.param_code }}
              </custom-table-cell>
              <custom-table-cell align="left">
                {{ props.row.param_name }}
              </custom-table-cell>
            </q-tr>
          </template>
        </CustomTable>
      </div>
    </div>
  </div>
</template>

<script>
import CustomTable from '@/components/common/CustomTable'
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell'
import { getFloorPlanTypes, getParamValueLinks, updateParamValueLink, deleteParamValueLink, getParamValueLinkStat } from './services'

export default {
  name: 'ParamValueLink',
  components: {
    CustomTable,
    CustomTableCell
  },
  data() {
    return {
      params: [],
      loadingParams: false,
      loadingLinks: false,
      saving: false,
      selectedParam: null,
      linkedMap: {},
      leftColumns: [
        {
          name: 'param_id',
          field: 'param_id',
          label: 'ID',
          align: 'center',
          sortable: true
        },
        {
          name: 'param_code',
          field: 'param_code',
          label: 'Формула',
          align: 'left',
          sortable: true
        },
        {
          name: 'param_name',
          field: 'param_name',
          label: 'Параметр',
          align: 'left',
          sortable: true
        }
      ],
      rightColumns: [
        {
          name: 'param_id',
          field: 'param_id',
          label: 'ID',
          align: 'center',
          sortable: true
        },
        {
          name: 'param_code',
          field: 'param_code',
          label: 'Формула',
          align: 'left',
          sortable: true
        },
        {
          name: 'param_name',
          field: 'param_name',
          label: 'Параметр',
          align: 'left',
          sortable: true
        }
      ],
      bulkInProgress: false,
      bulkTotal: 0,
      bulkDone: 0,
      linkStats: {},
      leftFilterHasLinksOnly: false,
      rightFilterLinkedOnly: false,
      rightSearch: ''
    }
  },
  async mounted() {
    await this.loadParams()
  },
  computed: {
    linkableRows() {
      if (!this.selectedParam) return []
      // правая таблица: все параметры кроме выбранного слева
      return this.params.filter(row => row.param_id !== this.selectedParam.param_id)
    },
    isAllLinked() {
      const rows = this.visibleRightRows
      if (!rows.length) return false
      return rows.every(row => !!this.linkedMap[row.param_id])
    },
    bulkProgress() {
      if (!this.bulkTotal) return 0
      return this.bulkDone / this.bulkTotal
    },
    visibleLeftRows() {
      if (!this.leftFilterHasLinksOnly) return this.params
      return this.params.filter(row => this.linkStats[row.param_id] > 0)
    },
    visibleRightRows() {
      let rows = this.linkableRows
      if (this.rightFilterLinkedOnly) {
        rows = rows.filter(row => this.isLinked(row))
      }
      if (this.rightSearch) {
        const term = this.rightSearch.toLowerCase()
        rows = rows.filter(row => {
          const name = (row.param_name || '').toLowerCase()
          const idStr = String(row.param_id || '').toLowerCase()
          const code = (row.param_code || '').toLowerCase()
          return name.includes(term) || idStr.includes(term) || code.includes(term)
        })
      }
      return rows
    }
  },
  methods: {
    async loadParams() {
      this.loadingParams = true
      const result = await getFloorPlanTypes()
      this.loadingParams = false
      if (!result) return
      this.params = result || []
      await this.loadStats()
    },
    async loadStats() {
      // статы по связкам получаем один раз
      const res = await getParamValueLinkStat()
      const stats = {}
      ;(res || []).forEach(item => {
        if (item.param_id) {
          stats[item.param_id] = item.link_count || 0
        }
      })
      this.linkStats = stats
    },
    async loadLinks() {
      if (!this.selectedParam?.param_id) {
        this.linkedMap = {}
        return
      }
      this.loadingLinks = true
      const res = await getParamValueLinks(this.selectedParam.param_id)
      this.loadingLinks = false
      if (!res) {
        this.linkedMap = {}
        return
      }
      const map = {}
      res.forEach(item => {
        if (item.param_link_id) {
          map[item.param_link_id] = item.param_value_link_id
        }
      })
      this.linkedMap = map
    },
    onSelectParam(row) {
      this.selectedParam = row
      this.loadLinks()
    },
    isLinked(row) {
      if (!this.selectedParam || row.param_id === this.selectedParam.param_id) return false
      return !!this.linkedMap[row.param_id]
    },
    hasLinks(row) {
      return (this.linkStats[row.param_id] || 0) > 0
    },
    async onToggleLink(row, value) {
      if (!this.selectedParam || row.param_id === this.selectedParam.param_id || this.saving) return

      this.saving = true
      try {
        if (value) {
          await updateParamValueLink({
            param_value_link_id: null,
            param_id: this.selectedParam.param_id,
            param_link_id: row.param_id
          })
        } else {
          const linkId = this.linkedMap[row.param_id]
          if (linkId) {
            await deleteParamValueLink(linkId)
          }
        }
        await this.loadLinks()
      } finally {
        this.saving = false
      }
    },
    async onToggleAll(value) {
      if (!this.selectedParam || this.saving || this.bulkInProgress) return

      const rows = this.visibleRightRows
      if (!rows.length) return

      this.bulkInProgress = true
      this.bulkTotal = rows.length
      this.bulkDone = 0

      try {
        for (const row of rows) {
          const currentlyLinked = this.isLinked(row)

          if (value && !currentlyLinked) {
            await this.onToggleLink(row, true)
          } else if (!value && currentlyLinked) {
            await this.onToggleLink(row, false)
          }

          this.bulkDone += 1
        }
      } finally {
        this.bulkInProgress = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.param-value-link {
  .tables-container {
    display: flex;
    gap: 16px;
  }

  .table-block {
    flex: 1;
    min-width: 0;
  }

  .clickable-row {
    cursor: pointer;
  }

  .selected-row {
    background-color: rgba(33, 150, 243, 0.1);
  }

  .bulk-progress {
    display: flex;
    align-items: center;
    gap: 8px;

    &__bar {
      flex: 1;
    }

    &__text {
      font-size: 11px;
      color: #666;
      white-space: nowrap;
    }
  }

  .right-filters {
    display: flex;
    align-items: center;
    gap: 8px;

    &__search {
      min-width: 180px;
    }
  }
}
</style>


